"use client";

import { FormEvent, useEffect, useMemo, useRef, useState, type PointerEvent } from "react";
import Link from "next/link";
import { useParams, useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { Archive, ChevronDown, Mail, MessageSquarePlus } from "lucide-react";

import SupportTicketMessageComposer from "@/app/profile/inbox/(chats)/components/SupportTickets/SupportTicketMessageComposer";
import {
	buildSupportTicketMessageDescription,
	hasSupportTicketMessageContent,
	supportTicketFormFieldTextClassName,
} from "@/app/profile/inbox/(chats)/components/SupportTickets/supportTicketMessageUtils";
import { cn, errorIntoString, formatRelativeTime } from "@/lib/utils";
import { uploadChatFile } from "@/lib/api/s3";
import { useToast } from "@/components/ui/use-toast";
import { ga4SupportTicketCreated } from "@/lib/analytics/ga4-ecommerce";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import {
	useCreateSupportTicketMutation,
	useArchiveSupportTicketMutation,
	useGetSupportTicketQuery,
	useGetSupportTicketsQuery,
	useMarkSupportTicketUnreadMutation,
	useReplySupportTicketMutation,
	useUpdateSupportTicketStatusMutation,
} from "@/features/support-tickets/supportTicketApiInjection";
import { useGetOrdersQuery } from "@/features/order/orderApiInjection";
import {
	SupportTicket,
	SupportTicketCategory,
	SupportTicketStatus,
	type CatalogSuggestion,
} from "@/types";
import { OrderStatus, type Order } from "@/types/orders";

const CATEGORY_OPTIONS = [
	{ value: SupportTicketCategory.ORDER, label: "Order" },
	{ value: SupportTicketCategory.HOSTING, label: "Hosting" },
	{ value: SupportTicketCategory.PLAN, label: "Plan" },
	{ value: SupportTicketCategory.OTHER, label: "Enquiry" },
];

const TECHXEED_SUPPORT_ICON = "/assets/solo%20icon.png";

function statusClasses(status: SupportTicketStatus) {
	return status === SupportTicketStatus.OPEN
		? "bg-[color:var(--color-brand-secondary)] text-white"
		: "bg-[color:var(--color-bg-light-grey)] text-white";
}

function SupportTicketStatusDropdown({
	status,
	disabled,
	onChange,
}: {
	status: SupportTicketStatus;
	disabled?: boolean;
	onChange: (status: SupportTicketStatus) => void | Promise<void>;
}) {
	const [open, setOpen] = useState(false);
	const menuRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (!open) return;

		function closeOnOutsidePointer(event: globalThis.PointerEvent) {
			if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
				setOpen(false);
			}
		}

		document.addEventListener("pointerdown", closeOnOutsidePointer);
		return () => document.removeEventListener("pointerdown", closeOnOutsidePointer);
	}, [open]);

	async function selectStatus(nextStatus: SupportTicketStatus) {
		setOpen(false);
		await onChange(nextStatus);
	}

	return (
		<div ref={menuRef} className="relative z-20 shrink-0">
			<button
				type="button"
				disabled={disabled}
				className={cn(
					"inline-flex w-[3.75rem] shrink-0 items-center justify-between rounded-full py-0.5 pl-2.5 pr-1.5 text-[10px] font-semibold transition-opacity hover:opacity-85 disabled:cursor-not-allowed disabled:opacity-60",
					statusClasses(status),
				)}
				aria-label="Change support ticket status"
				onClick={() => setOpen((current) => !current)}
			>
				{status}
				<ChevronDown className={cn("ml-1 size-3 transition-transform", open && "rotate-180")} aria-hidden />
			</button>
			{open ? (
				<div className="absolute bottom-full left-1/2 mb-2 w-28 -translate-x-1/2 overflow-hidden rounded-2xl bg-background shadow-lg ring-1 ring-border">
					<button
						type="button"
						disabled={disabled || status === SupportTicketStatus.OPEN}
						className="block w-full px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted disabled:text-muted-foreground"
						onClick={() => selectStatus(SupportTicketStatus.OPEN)}
					>
						Open
					</button>
					<button
						type="button"
						disabled={disabled || status === SupportTicketStatus.CLOSED}
						className="block w-full px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted disabled:text-muted-foreground"
						onClick={() => selectStatus(SupportTicketStatus.CLOSED)}
					>
						Closed
					</button>
				</div>
			) : null}
		</div>
	);
}

function formatTicketDate(date?: string) {
	if (!date) return "";
	return new Intl.DateTimeFormat("en", {
		month: "short",
		day: "numeric",
		hour: "2-digit",
		minute: "2-digit",
	}).format(new Date(date));
}

function formatTicketListTime(date?: string) {
	if (!date) return "";
	return formatRelativeTime(date);
}

function ticketCategoryLabel(category: SupportTicket["category"]) {
	return category === SupportTicketCategory.OTHER ? "ENQUIRY" : category;
}

function ticketTitle(ticket: Pick<SupportTicket, "ticketNumber" | "_id" | "category">) {
	return `Support Ticket #${(ticket.ticketNumber || ticket._id.slice(-5)).toUpperCase()}`;
}

type SupportTicketPerson = {
	firstName?: string | null;
	lastName?: string | null;
	email?: string | null;
} | null | undefined;

function personName(person?: SupportTicketPerson) {
	const fullName = `${person?.firstName ?? ""} ${person?.lastName ?? ""}`.trim();
	return fullName || person?.email || "Unknown customer";
}

function personLabel(person?: SupportTicketPerson) {
	const name = personName(person);
	return person?.email ? `${name} (${person.email})` : name;
}

function idToString(value: unknown) {
	if (!value) return "";
	if (typeof value === "string") return value;
	if (typeof value === "object" && "_id" in value) {
		return idToString((value as { _id?: unknown })._id);
	}
	if (typeof value === "object" && "id" in value) {
		return idToString((value as { id?: unknown }).id);
	}
	return String(value);
}

function isStaffSideReply(reply: SupportTicket["replies"][number]) {
	return reply.isStaffReply === true;
}

function orderServiceLabel(order?: Pick<SupportTicket, "orderId">["orderId"], orderItemId?: string) {
	const orderId = order?._id ?? order?.id;
	if (orderItemId) {
		const itemTitle = order?.items?.find((item) => item._id === orderItemId || (item as any).id === orderItemId)?.serviceId?.title;
		if (itemTitle?.trim()) return itemTitle;
	}
	const serviceNames =
		order?.items
			?.map((item) => item.serviceId?.title)
			.filter((title): title is string => Boolean(title?.trim())) ?? [];
	if (serviceNames.length === 0) {
		return orderId ? `Order #${orderId.slice(-6).toUpperCase()}` : "No specific order";
	}
	return serviceNames.join(", ");
}

function adminOrderHref(order?: Pick<SupportTicket, "orderId">["orderId"], orderItemId?: string) {
	const orderId = order?._id ?? order?.id;
	if (!orderId) return undefined;
	const params = new URLSearchParams({ relatedOrder: orderId });
	if (orderItemId) params.set("relatedOrderItem", orderItemId);
	return `/admin/orders?${params.toString()}`;
}

type RelatedOrderOption = {
	value: string;
	orderId: string;
	orderItemId?: string;
	label: string;
	createdAt: string;
};

function buildRelatedOrderOptions(orders: Order[]): RelatedOrderOption[] {
	return orders.flatMap((order) => {
		const orderId = order.id;
		const itemOptions =
			order.items
				?.flatMap((item): RelatedOrderOption[] => {
					const itemId = item._id ?? (item as any).id;
					const label = item.serviceId?.title?.trim();
					if (!itemId || !label) return [];
					return [
						{
							value: `${orderId}:${itemId}`,
							orderId,
							orderItemId: itemId,
							label,
							createdAt: order.createdAt,
						},
					];
				}) ?? [];
		if (itemOptions.length > 0) return itemOptions;
		return [
			{
				value: orderId,
				orderId,
				label: orderServiceLabel(order),
				createdAt: order.createdAt,
			},
		];
	});
}

function latestMessagePreview(ticket: SupportTicket) {
	const latestMessage = ticket.replies?.[ticket.replies.length - 1]?.message || ticket.description || "";
	const trimmed = latestMessage.trim();
	if (!trimmed) return "";
	return trimmed.endsWith("...") ? trimmed : `${trimmed}...`;
}

function hasIncomingSupportReply(
	ticket: SupportTicket,
	staffMode: boolean,
	viewedAtOverride?: string,
) {
	if (ticket.status === SupportTicketStatus.CLOSED) return false;
	const latestReply = ticket.replies?.[ticket.replies.length - 1];
	if (!latestReply) return false;
	const isStaffReply = isStaffSideReply(latestReply);
	const isIncomingReply = staffMode ? !isStaffReply : isStaffReply;
	if (!isIncomingReply) return false;
	const viewedAt = viewedAtOverride ?? (staffMode ? ticket.staffLastViewedAt : ticket.userLastViewedAt);
	if (!viewedAt) return true;
	return new Date(latestReply.createdAt).getTime() > new Date(viewedAt).getTime();
}

function hasAnyIncomingSupportReply(ticket: SupportTicket, staffMode: boolean) {
	return (ticket.replies ?? []).some((reply) => {
		const isStaffReply = isStaffSideReply(reply);
		return staffMode ? !isStaffReply : isStaffReply;
	});
}

type SupportTicketsPanelProps = {
	staffMode?: boolean;
	compactStaffMode?: boolean;
	className?: string;
	showCreateButton?: boolean;
	archived?: boolean;
	userId?: string;
	edgeToEdgeRows?: boolean;
};

export default function SupportTicketsPanel({
	staffMode = false,
	compactStaffMode = false,
	className,
	showCreateButton = true,
	archived = false,
	userId,
	edgeToEdgeRows = false,
}: SupportTicketsPanelProps) {
	const { data: session, status } = useSession();
	const { toast } = useToast();
	const router = useRouter();
	const params = useParams<{ ticketId?: string }>();
	const [statusFilter, setStatusFilter] = useState<SupportTicketStatus | undefined>(undefined);
	const [createOpen, setCreateOpen] = useState(false);
	const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
	const [locallyViewedTickets, setLocallyViewedTickets] = useState<Record<string, string>>({});
	const [archiveTicket, { isLoading: isArchiving }] = useArchiveSupportTicketMutation();
	const [markTicketUnread, { isLoading: isMarkingUnread }] = useMarkSupportTicketUnreadMutation();

	const tickets = useGetSupportTicketsQuery(
		{
			limit: staffMode ? 25 : 5,
			status: archived ? undefined : statusFilter,
			archived,
			userId,
			sortBy: "lastActivityAt:desc",
		},
		{ skip: status !== "authenticated" || !session?.idToken },
	);

	const docs = useMemo(() => {
		const ticketDocs = tickets.data?.docs ?? [];
		const visibleTickets = !archived || !userId
			? ticketDocs
			: ticketDocs.filter((ticket) => ticket.archivedFor?.includes(userId));
		return [...visibleTickets].sort(
			(first, second) =>
				new Date(second.lastActivityAt ?? second.updatedAt).getTime() -
				new Date(first.lastActivityAt ?? first.updatedAt).getTime(),
		);
	}, [archived, tickets.data?.docs, userId]);

	useEffect(() => {
		if (!staffMode) return;
		if (!selectedTicketId) return;
		if (docs.length === 0 || !docs.some((ticket) => ticket._id === selectedTicketId)) {
			setSelectedTicketId(null);
		}
	}, [docs, selectedTicketId, staffMode]);

	async function onArchiveTicket(ticketId: string) {
		try {
			await archiveTicket(ticketId).unwrap();
			toast({ title: "Ticket archived" });
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Could not archive ticket",
				description: errorIntoString(error),
			});
		}
	}

	async function onMarkTicketUnread(ticketId: string) {
		try {
			await markTicketUnread({
				id: ticketId,
				viewAs: staffMode ? "staff" : "user",
			}).unwrap();
			setLocallyViewedTickets((current) => {
				const next = { ...current };
				delete next[ticketId];
				return next;
			});
			toast({ title: "Ticket marked unread" });
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Could not mark ticket unread",
				description: errorIntoString(error),
			});
		}
	}

	function markTicketLocallyViewed(ticket: SupportTicket) {
		const latestReply = ticket.replies?.[ticket.replies.length - 1];
		const viewedAt = latestReply?.createdAt
			? new Date(new Date(latestReply.createdAt).getTime() + 1).toISOString()
			: new Date().toISOString();
		setLocallyViewedTickets((current) => ({
			...current,
			[ticket._id]: viewedAt,
		}));
	}

	useEffect(() => {
		const activeTicketId = staffMode ? selectedTicketId : params.ticketId;
		if (!activeTicketId) return;
		const activeTicket = docs.find((ticket) => ticket._id === activeTicketId);
		if (!activeTicket) return;
		const latestReply = activeTicket.replies?.[activeTicket.replies.length - 1];
		const viewedAt = latestReply?.createdAt
			? new Date(new Date(latestReply.createdAt).getTime() + 1).toISOString()
			: new Date().toISOString();
		if (locallyViewedTickets[activeTicket._id] === viewedAt) return;
		setLocallyViewedTickets((current) => ({
			...current,
			[activeTicket._id]: viewedAt,
		}));
	}, [docs, locallyViewedTickets, params.ticketId, selectedTicketId, staffMode]);

	if (staffMode && !compactStaffMode) {
		return (
			<section className={cn("bg-background/80", className)}>
				<div className="grid min-h-[calc(100vh-13rem)] overflow-hidden rounded-2xl border border-border/70 bg-background md:grid-cols-[22rem_1fr]">
					<aside className="border-r border-[color:var(--color-border-standard)] bg-background/90">
						<div className="border-b border-[color:var(--color-border-standard)] p-4">
							<div className="space-y-1">
								<h3 className="text-sm font-semibold text-foreground">Support Tickets</h3>
								<p className="text-xs text-muted-foreground">Review your support requests</p>
							</div>
							{archived ? null : (
								<div className="mt-3 inline-flex items-center gap-4">
									{[undefined, SupportTicketStatus.OPEN, SupportTicketStatus.CLOSED].map((value) => (
										<button
											key={value ?? "ALL"}
											type="button"
											className={cn(
												"text-xs font-medium transition-colors",
												statusFilter === value
													? "text-secondary"
													: "text-muted-foreground hover:text-secondary",
											)}
											onClick={() => setStatusFilter(value)}
										>
											{value === SupportTicketStatus.OPEN
												? "Open"
												: value === SupportTicketStatus.CLOSED
													? "Closed"
													: "All"}
										</button>
									))}
								</div>
							)}
						</div>
						<div className="max-h-[calc(100vh-18rem)] overflow-y-auto py-2">
							{tickets.isLoading ? (
								<div className="space-y-2 px-3 py-3">
									{Array.from({ length: 5 }).map((_, index) => (
										<div key={index} className="h-20 animate-pulse rounded-lg bg-secondary/10" />
									))}
								</div>
							) : docs.length === 0 ? (
								<p className="px-4 py-6 text-xs text-muted-foreground">
									{archived ? "No archived support tickets found" : "No tickets yet."}
								</p>
							) : (
								docs.map((ticket) => (
									<SupportTicketRow
										key={ticket._id}
										ticket={ticket}
										archived={archived}
										isSelected={selectedTicketId === ticket._id}
										staffMode={staffMode}
										viewedAtOverride={locallyViewedTickets[ticket._id]}
										isBusy={isArchiving || isMarkingUnread}
										onOpen={() => {
											markTicketLocallyViewed(ticket);
											setSelectedTicketId(ticket._id);
										}}
										onArchive={() => onArchiveTicket(ticket._id)}
										onMarkUnread={() => onMarkTicketUnread(ticket._id)}
									/>
								))
							)}
						</div>
					</aside>
					<SupportTicketStaffThread ticketId={selectedTicketId} />
				</div>
			</section>
		);
	}

	return (
		<section className={cn("bg-background/80 pb-3 pt-5", className)}>
			<div className={cn("mb-4 flex items-center justify-between gap-2", (!staffMode || compactStaffMode) && "px-3")}>
				<div className="min-w-0 flex-1 space-y-1">
					<h3 className="text-sm font-semibold text-foreground">
						Support Tickets
					</h3>
					<p className="truncate text-xs text-muted-foreground">
						Review your support requests
					</p>
				</div>
				{staffMode || !showCreateButton ? null : (
					<SupportTicketCreateButton onClick={() => setCreateOpen(true)} />
				)}
			</div>

			{archived ? null : (
				<div className={cn("mb-3 flex justify-start", (!staffMode || compactStaffMode) && "px-3")}>
					<div className="inline-flex items-center gap-4">
						{[undefined, SupportTicketStatus.OPEN, SupportTicketStatus.CLOSED].map((value) => (
							<button
								key={value ?? "ALL"}
								type="button"
								className={cn(
									"text-xs font-medium transition-colors",
									statusFilter === value
										? "text-secondary"
										: "text-muted-foreground hover:text-secondary",
								)}
								onClick={() => setStatusFilter(value)}
							>
								{value === SupportTicketStatus.OPEN
									? "Open"
									: value === SupportTicketStatus.CLOSED
										? "Closed"
										: "All"}
							</button>
						))}
					</div>
				</div>
			)}

			<div>
				{tickets.isLoading ? (
					<p className="py-4 text-center text-xs text-muted-foreground">Loading tickets...</p>
				) : docs.length === 0 ? (
					<p className={cn("py-3 text-xs text-muted-foreground", (!staffMode || compactStaffMode) && "px-3")}>
						{archived ? "No archived support tickets found" : "No tickets yet."}
					</p>
				) : (
					docs.map((ticket) => (
						<SupportTicketRow
							key={ticket._id}
							ticket={ticket}
							archived={archived}
							isSelected={params.ticketId === ticket._id}
							staffMode={staffMode}
							viewedAtOverride={locallyViewedTickets[ticket._id]}
							isBusy={isArchiving || isMarkingUnread}
							edgeToEdge={edgeToEdgeRows}
							onOpen={() => {
								markTicketLocallyViewed(ticket);
								if (staffMode) {
									setSelectedTicketId(ticket._id);
									return;
								}
								router.push(`/profile/inbox/ticket/${ticket._id}`);
							}}
							onArchive={() => onArchiveTicket(ticket._id)}
							onMarkUnread={() => onMarkTicketUnread(ticket._id)}
						/>
					))
				)}
			</div>

			<NewTicketDialog open={createOpen} onOpenChange={setCreateOpen} />
			<TicketDetailsDialog
				ticketId={selectedTicketId}
				staffMode={staffMode}
				onOpenChange={(open) => {
					if (!open) setSelectedTicketId(null);
				}}
			/>
		</section>
	);
}

export function SupportTicketCreateButton({ onClick }: { onClick: () => void }) {
	return (
		<Button
			type="button"
			size="sm"
			className="h-7 w-auto shrink-0 whitespace-nowrap rounded-full bg-secondary px-2 text-[0.65rem] leading-none text-white hover:bg-secondary/90 sm:h-8 sm:px-3 sm:text-xs"
			onClick={onClick}
			aria-label="New Ticket"
		>
			<MessageSquarePlus className="mr-1 size-2.5 shrink-0 !text-white !stroke-white sm:mr-1.5 sm:size-3.5" />
			<span className="whitespace-nowrap leading-none [transform:translateY(0.5px)]">New Ticket</span>
		</Button>
	);
}

function SupportTicketRow({
	ticket,
	archived,
	isSelected,
	staffMode,
	viewedAtOverride,
	isBusy,
	edgeToEdge = false,
	onOpen,
	onArchive,
	onMarkUnread,
}: {
	ticket: SupportTicket;
	archived: boolean;
	isSelected: boolean;
	staffMode: boolean;
	viewedAtOverride?: string;
	isBusy: boolean;
	edgeToEdge?: boolean;
	onOpen: () => void;
	onArchive: () => Promise<void>;
	onMarkUnread: () => Promise<void>;
}) {
	const pointerStartXRef = useRef<number | null>(null);
	const dragXRef = useRef(0);
	const didSwipeRef = useRef(false);
	const [dragX, setDragX] = useState(0);
	const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
	const hasIncomingReply =
		!isSelected &&
		hasIncomingSupportReply(ticket, staffMode, viewedAtOverride);
	const canMarkUnread =
		!archived &&
		ticket.status !== SupportTicketStatus.CLOSED &&
		hasAnyIncomingSupportReply(ticket, staffMode) &&
		!hasIncomingReply;

	const startSwipe = (clientX: number) => {
		if ((archived && !canMarkUnread) || isBusy) return;
		pointerStartXRef.current = clientX;
		dragXRef.current = 0;
		didSwipeRef.current = false;
	};

	const moveSwipe = (clientX: number) => {
		if (isBusy || pointerStartXRef.current == null) return;
		const deltaX = clientX - pointerStartXRef.current;
		if (deltaX < 0) {
			if (archived) return;
			const nextDragX = Math.max(deltaX, -96);
			dragXRef.current = nextDragX;
			setDragX(nextDragX);
			if (Math.abs(nextDragX) > 8) didSwipeRef.current = true;
			return;
		}
		if (deltaX > 0 && canMarkUnread) {
			const nextDragX = Math.min(deltaX, 96);
			dragXRef.current = nextDragX;
			setDragX(nextDragX);
			if (Math.abs(nextDragX) > 8) didSwipeRef.current = true;
			return;
		}
		dragXRef.current = 0;
		setDragX(0);
	};

	const endSwipe = () => {
		if (!archived && !isBusy && dragXRef.current <= -56) {
			setArchiveConfirmOpen(true);
		} else if (canMarkUnread && !isBusy && dragXRef.current >= 56) {
			void onMarkUnread();
		}
		pointerStartXRef.current = null;
		dragXRef.current = 0;
		setDragX(0);
	};

	const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
		startSwipe(event.clientX);
		if (event.pointerType !== "mouse") {
			event.currentTarget.setPointerCapture?.(event.pointerId);
		}
	};

	const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
		moveSwipe(event.clientX);
		if (didSwipeRef.current) {
			event.preventDefault();
		}
	};

	const onPointerEnd = (event: PointerEvent<HTMLDivElement>) => {
		endSwipe();
		event.currentTarget.releasePointerCapture?.(event.pointerId);
	};

	return (
		<>
			<div className="group relative">
				{!archived && dragX < 0 ? (
					<div className="pointer-events-none absolute inset-y-0 right-2 flex w-28 items-center justify-end rounded-r-lg bg-muted pr-4 text-muted-foreground">
						<span className="inline-flex items-center gap-1.5 text-xs font-medium">
							<Archive className="size-4" />
							Archive
						</span>
					</div>
				) : null}
				{canMarkUnread && dragX > 0 ? (
					<div className="pointer-events-none absolute inset-y-0 left-2 flex w-28 items-center justify-start rounded-l-lg bg-accent-foreground/10 pl-4 text-tertiary">
						<span className="inline-flex items-center gap-1.5 text-xs font-medium">
							<Mail className="size-4" />
							Unread
						</span>
					</div>
				) : null}
				<div
					className={cn("relative touch-pan-y", dragX === 0 && "transition-transform")}
					style={dragX !== 0 ? { transform: `translateX(${dragX}px)` } : undefined}
					onPointerDown={onPointerDown}
					onPointerMove={onPointerMove}
					onPointerUp={onPointerEnd}
					onPointerCancel={onPointerEnd}
					onMouseLeave={() => {
						if (pointerStartXRef.current != null) endSwipe();
					}}
				>
					<button
						type="button"
						className={cn(
							"group grid h-20 grid-cols-[3.5rem_1fr] items-center gap-2 p-2 text-left transition-all hover:bg-accent-foreground/5",
							edgeToEdge ? "relative mx-0 w-full rounded-none after:absolute after:bottom-0 after:left-[4.5rem] after:right-0 after:h-px after:bg-[color:var(--color-border-standard)]" : "mx-2 w-[calc(100%-1rem)] rounded-lg",
							isSelected && !edgeToEdge && "bg-tertiary/10",
							hasIncomingReply && "bg-accent-foreground/10",
						)}
						onClickCapture={(event) => {
							if (didSwipeRef.current) {
								event.preventDefault();
								didSwipeRef.current = false;
								return;
							}
							onOpen();
						}}
					>
						<div className="relative size-14">
							<div
								className={cn(
									"flex size-14 items-center justify-center rounded-md",
									hasIncomingReply
										? "bg-accent-foreground"
										: ticket.status === SupportTicketStatus.CLOSED
										? "bg-muted"
										: "bg-[color:var(--color-brand-primary)]",
								)}
							>
								<img
									className="size-10 object-contain brightness-0 invert"
									src={TECHXEED_SUPPORT_ICON}
									alt=""
									draggable={false}
									aria-hidden
								/>
							</div>
						</div>

						<div className="relative flex min-w-0 flex-1 flex-col justify-center">
							<div className="flex items-center gap-2">
								{hasIncomingReply ? (
									<span className="size-2 shrink-0 rounded-full bg-accent-foreground" aria-hidden />
								) : null}
								<h3
									className={cn(
										"truncate text-sm2 font-semibold text-muted-foreground",
										hasIncomingReply && "text-accent-foreground",
									)}
								>
									{ticketTitle(ticket)}
								</h3>
								<span
									className={cn(
										"ml-auto w-[3.25rem] shrink-0 rounded-full px-2 py-0.5 text-center text-[10px] font-semibold",
										statusClasses(ticket.status),
									)}
								>
									{ticket.status}
								</span>
							</div>
							<div className="flex items-center gap-2">
								<p
									className={cn(
										"min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground/90",
										hasIncomingReply && "text-accent-foreground",
									)}
								>
									{ticket.subject}
								</p>
								<span className="w-14 shrink-0" aria-hidden />
							</div>
							<div className="flex items-center gap-2">
								<p
									className={cn(
										"min-w-0 flex-1 truncate text-xs text-muted-foreground",
										hasIncomingReply && "font-semibold text-accent-foreground",
									)}
								>
									{latestMessagePreview(ticket)}
								</p>
								<span className="w-8 shrink-0" aria-hidden />
								<span className="shrink-0 text-xs text-muted-foreground">
									{formatTicketListTime(ticket.lastActivityAt ?? ticket.updatedAt)}
								</span>
							</div>
						</div>
					</button>
					{edgeToEdge ? null : (
						<span className="pointer-events-none ml-[4.5rem] mr-2 block h-px bg-[color:var(--color-border-standard)]" />
					)}
				</div>
			</div>
			<Dialog open={archiveConfirmOpen} onOpenChange={setArchiveConfirmOpen}>
				<DialogContent
					hideClose
					className="w-[calc(100vw-2rem)] max-w-sm rounded-3xl border border-[color:var(--glass-border)] bg-white/70 p-6 shadow-[0_18px_60px_rgba(0,0,0,0.18)] backdrop-blur-2xl dark:border-white/15 dark:bg-[hsl(var(--popover)_/_0.62)]"
				>
					<DialogHeader className="space-y-2 text-left">
						<DialogTitle className="text-xl font-semibold text-foreground">
							Archive ticket?
						</DialogTitle>
						<DialogDescription className="text-sm text-muted-foreground">
							This moves the ticket out of your Support list and into Archive.
						</DialogDescription>
					</DialogHeader>
					<DialogFooter className="mt-4 flex-row justify-end gap-2 space-x-0">
						<Button
							type="button"
							variant="ghost"
							className="rounded-full px-5 text-secondary hover:bg-secondary/10 hover:text-secondary"
							onClick={() => setArchiveConfirmOpen(false)}
						>
							Cancel
						</Button>
						<Button
							type="button"
							className="rounded-full bg-secondary px-5 text-secondary-foreground hover:bg-secondary/90"
							disabled={isBusy}
							onClick={async () => {
								await onArchive();
								setArchiveConfirmOpen(false);
							}}
						>
							Archive
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}

function NewTicketDialog({
	open,
	onOpenChange,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const router = useRouter();
	const { data: session } = useSession();
	const { toast } = useToast();
	const [category, setCategory] = useState<SupportTicketCategory | "">("");
	const [orderId, setOrderId] = useState("");
	const [orderItemId, setOrderItemId] = useState("");
	const [relatedOrderValue, setRelatedOrderValue] = useState("");
	const [subject, setSubject] = useState("");
	const [description, setDescription] = useState("");
	const [messageFiles, setMessageFiles] = useState<File[]>([]);
	const [catalogSuggestions, setCatalogSuggestions] = useState<CatalogSuggestion[]>([]);
	const [formError, setFormError] = useState("");
	const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
	const [orderDropdownOpen, setOrderDropdownOpen] = useState(false);
	const [createTicket, createState] = useCreateSupportTicketMutation();
	const orders = useGetOrdersQuery(
		{
			userId: session?.user.id,
			limit: 50,
			orderStatus: OrderStatus.PAID,
		},
		{ skip: !session?.user.id },
	);

	const orderOptions = useMemo(() => orders.data?.docs ?? [], [orders.data?.docs]);
	const relatedOrderOptions = useMemo(() => buildRelatedOrderOptions(orderOptions), [orderOptions]);
	const selectedOrderOption = relatedOrderOptions.find((order) => order.value === relatedOrderValue);

	useEffect(() => {
		if (!open) {
			setCategory("");
			setOrderId("");
			setOrderItemId("");
			setRelatedOrderValue("");
			setSubject("");
			setDescription("");
			setMessageFiles([]);
			setCatalogSuggestions([]);
			setFormError("");
			setCategoryDropdownOpen(false);
			setOrderDropdownOpen(false);
		}
	}, [open]);

	async function onSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		const ticketDescription = buildSupportTicketMessageDescription(
			description,
			catalogSuggestions,
		);
		if (!category) {
			setFormError("Select a ticket type before creating the ticket.");
			return;
		}
		if (
			!subject.trim() ||
			!hasSupportTicketMessageContent(
				description,
				messageFiles.length,
				catalogSuggestions.length,
			)
		) {
			setFormError("Add a subject and message before creating the ticket.");
			return;
		}
		setFormError("");
		try {
			const uploadEntityId = session?.user.id ?? "support-ticket";
			const fileUrls = await Promise.all(
				messageFiles.map((file) =>
					uploadChatFile(file, {
						entityId: uploadEntityId,
						filePurpose: "attachment",
					}),
				),
			);
			const createdTicket = await createTicket({
				category,
				orderId: category === SupportTicketCategory.ORDER ? orderId || undefined : undefined,
				orderItemId: category === SupportTicketCategory.ORDER ? orderItemId || undefined : undefined,
				subject: subject.trim(),
				description: ticketDescription || "Attachment",
				fileUrls,
			}).unwrap();
			ga4SupportTicketCreated({ ticket_id: createdTicket._id, category });
			toast({ title: "Ticket created", description: "Admin staff can now review it." });
			setCategory("");
			setOrderId("");
			setOrderItemId("");
			setRelatedOrderValue("");
			setSubject("");
			setDescription("");
			setMessageFiles([]);
			setCatalogSuggestions([]);
			onOpenChange(false);
			router.push(`/profile/inbox/ticket/${createdTicket._id}`);
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Could not create ticket",
				description: errorIntoString(error),
			});
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent
				hideClose
				className="border border-secondary/25 bg-white/90 text-foreground shadow-2xl shadow-secondary/20 backdrop-blur-3xl dark:bg-background/45 sm:max-w-lg"
				onInteractOutside={(event) => event.preventDefault()}
				onPointerDownOutside={(event) => event.preventDefault()}
				onEscapeKeyDown={(event) => event.preventDefault()}
			>
				<DialogHeader>
					<DialogTitle className="text-foreground">New Support Ticket</DialogTitle>
					<DialogDescription className="text-muted-foreground">
						Create a support ticket for orders, hosting, plans, or an enquiry.
					</DialogDescription>
				</DialogHeader>
				<form className="space-y-4" noValidate onSubmit={onSubmit}>
					{formError ? (
						<div className="rounded-2xl border border-secondary/20 bg-secondary/10 px-4 py-2 text-sm text-secondary">
							{formError}
						</div>
					) : null}
					<label className="grid gap-1.5 text-sm">
						<span className="text-xs font-medium text-muted-foreground">Type</span>
						<div className="relative">
							<button
								type="button"
								className={cn(
									"relative flex h-10 w-full items-center rounded-md border border-secondary/35 bg-white/85 px-3 pr-10 text-left shadow-sm outline-none backdrop-blur-2xl transition-colors hover:border-secondary hover:bg-secondary/10 dark:bg-background/25",
									supportTicketFormFieldTextClassName,
								)}
								onClick={() => setCategoryDropdownOpen((open) => !open)}
							>
								<span className={cn("min-w-0 truncate", !category && "text-muted-foreground")}>
									{category
										? CATEGORY_OPTIONS.find((option) => option.value === category)?.label
										: "Select"}
								</span>
								<span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
									<ChevronDown
										className={cn(
											"size-4 stroke-secondary transition-transform",
											categoryDropdownOpen && "rotate-180",
										)}
										aria-hidden
									/>
								</span>
							</button>
							{categoryDropdownOpen ? (
								<div className="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-secondary/35 bg-white/95 p-1 shadow-2xl shadow-secondary/25 backdrop-blur-[32px] dark:bg-background/80">
									{CATEGORY_OPTIONS.map((option) => (
										<button
											key={option.value}
											type="button"
											className={cn(
												"block w-full rounded-lg px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-secondary/15",
												option.value === category && "bg-secondary text-secondary-foreground hover:bg-secondary",
											)}
											onClick={() => {
												setCategory(option.value);
												if (option.value !== SupportTicketCategory.ORDER) {
													setOrderId("");
													setOrderItemId("");
													setRelatedOrderValue("");
												}
												setCategoryDropdownOpen(false);
											}}
										>
											{option.label}
										</button>
									))}
								</div>
							) : null}
						</div>
					</label>
					{category === SupportTicketCategory.ORDER ? (
						<label className="grid gap-1.5 text-sm">
							<span className="text-xs font-medium text-muted-foreground">Related order</span>
							<div className="relative">
								<button
									type="button"
									className={cn(
									"relative flex h-10 w-full items-center rounded-md border border-secondary/35 bg-white/85 px-3 pr-10 text-left shadow-sm outline-none backdrop-blur-2xl transition-colors hover:border-secondary hover:bg-secondary/10 dark:bg-background/25",
									supportTicketFormFieldTextClassName,
								)}
									onClick={() => setOrderDropdownOpen((open) => !open)}
								>
									<span className="min-w-0 truncate">
										{selectedOrderOption ? selectedOrderOption.label : "No specific order"}
									</span>
									<span className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
										<ChevronDown
											className={cn(
												"size-4 stroke-secondary transition-transform",
												orderDropdownOpen && "rotate-180",
											)}
											aria-hidden
										/>
									</span>
								</button>
								{orderDropdownOpen ? (
									<div className="absolute z-50 mt-1 max-h-52 w-full overflow-auto rounded-xl border border-secondary/35 bg-white/95 p-1 shadow-2xl shadow-secondary/25 backdrop-blur-[32px] dark:bg-background/80">
										<button
											type="button"
											className={cn(
												"block w-full rounded-lg px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-secondary/15",
												orderId === "" && "bg-secondary text-secondary-foreground hover:bg-secondary",
											)}
											onClick={() => {
												setOrderId("");
												setOrderItemId("");
												setRelatedOrderValue("");
												setOrderDropdownOpen(false);
											}}
										>
											No specific order
										</button>
										{relatedOrderOptions.map((order) => {
											return (
												<button
													key={order.value}
													type="button"
													className={cn(
														"block w-full rounded-lg px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-secondary/15",
														relatedOrderValue === order.value &&
															"bg-secondary text-secondary-foreground hover:bg-secondary",
													)}
													onClick={() => {
														setOrderId(order.orderId);
														setOrderItemId(order.orderItemId ?? "");
														setRelatedOrderValue(order.value);
														setOrderDropdownOpen(false);
													}}
												>
													<span className="block truncate font-medium">{order.label}</span>
													<span className="block truncate text-xs text-muted-foreground">
														{formatTicketDate(order.createdAt)}
													</span>
												</button>
											);
										})}
									</div>
								) : null}
							</div>
						</label>
					) : null}
					<label className="grid gap-1.5 text-sm">
						<span className="text-xs font-medium text-muted-foreground">Subject</span>
						<Input
							value={subject}
							onChange={(event) => {
								setSubject(event.target.value);
								setFormError("");
							}}
							placeholder="Briefly explain the issue"
							className={cn(
								"border-secondary/35 bg-white/85 shadow-sm backdrop-blur-2xl placeholder:text-muted-foreground focus-visible:ring-secondary/30 dark:bg-background/25",
								supportTicketFormFieldTextClassName,
							)}
						/>
					</label>
					<div className="grid gap-1.5 text-sm">
						<span className="text-xs font-medium text-muted-foreground">Message</span>
						<SupportTicketMessageComposer
							value={description}
							onChange={(value) => {
								setDescription(value);
								setFormError("");
							}}
							files={messageFiles}
							onFilesChange={setMessageFiles}
							catalogSuggestions={catalogSuggestions}
							onCatalogSuggestionsChange={setCatalogSuggestions}
						/>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							Cancel
						</Button>
						<Button
							type="submit"
							className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
							disabled={createState.isLoading}
						>
							Create Ticket
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}

function SupportTicketStaffThread({ ticketId }: { ticketId: string | null }) {
	const { toast } = useToast();
	const { data: session } = useSession();
	const [reply, setReply] = useState("");
	const ticketQuery = useGetSupportTicketQuery(
		{ id: ticketId ?? "", viewAs: "staff" },
		{ skip: !ticketId },
	);
	const [replyTicket, replyState] = useReplySupportTicketMutation();
	const [updateStatus, updateStatusState] = useUpdateSupportTicketStatusMutation();
	const ticket = ticketQuery.data;
	const orderLink = adminOrderHref(ticket?.orderId, ticket?.orderItemId);

	async function submitReply(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!ticket || !reply.trim()) return;
		try {
			await replyTicket({ id: ticket._id, message: reply.trim(), isStaffReply: true }).unwrap();
			setReply("");
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Could not send reply",
				description: errorIntoString(error),
			});
		}
	}

	async function setStatus(status: SupportTicketStatus) {
		if (!ticket) return;
		try {
			await updateStatus({ id: ticket._id, status }).unwrap();
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Could not update status",
				description: errorIntoString(error),
			});
		}
	}

	if (!ticketId) {
		return (
			<div className="flex min-h-full items-center justify-center bg-background p-8 text-center text-sm text-muted-foreground">
				Select a ticket to view the support conversation.
			</div>
		);
	}

	if (ticketQuery.isLoading || !ticket) {
		return (
			<div className="flex min-h-full flex-col bg-background p-4">
				<div className="mb-4 h-12 animate-pulse rounded-xl bg-secondary/10" />
				<div className="h-32 animate-pulse rounded-3xl bg-secondary/10" />
				<div className="mt-6 h-10 w-1/2 animate-pulse rounded-2xl bg-secondary/15" />
				<div className="mt-3 h-10 w-1/2 animate-pulse self-end rounded-2xl bg-secondary/20" />
			</div>
		);
	}

	return (
		<div className="flex min-h-full flex-col bg-background">
			<div className="flex min-h-16 items-center justify-between gap-3 border-b border-[color:var(--color-border-standard)] px-5 py-3">
				<div className="min-w-0">
					<h2 className="truncate text-sm font-semibold text-foreground">{ticketTitle(ticket)}</h2>
					<p className="truncate text-xs text-muted-foreground">
						{ticket.category} | {personName(ticket.userId)} | Opened on{" "}
						{formatTicketDate(ticket.createdAt)}
					</p>
				</div>
				<span
					className={cn(
						"w-[3.25rem] shrink-0 rounded-full px-2 py-0.5 text-center text-[10px] font-semibold",
						statusClasses(ticket.status),
					)}
				>
					{ticket.status}
				</span>
			</div>

			<div className="flex-1 space-y-4 overflow-y-auto p-5">
				<div className="mx-auto w-full max-w-xl rounded-3xl border border-[color:var(--color-border-standard)] bg-background/85 p-5 shadow-sm">
					<h3 className="mb-4 text-sm font-semibold text-foreground">Ticket Details</h3>
					<div className="grid gap-3 text-sm">
						<div>
							<p className="text-xs font-medium text-muted-foreground">Customer</p>
							<p className="font-semibold text-foreground">
								{personLabel(ticket.userId)}
							</p>
						</div>
						<div>
							<p className="text-xs font-medium text-muted-foreground">Type</p>
							<p className="font-semibold text-foreground">{ticket.category === "OTHER" ? "ENQUIRY" : ticket.category}</p>
						</div>
						{ticket.orderId ? (
							<div>
								<p className="text-xs font-medium text-muted-foreground">Related order</p>
								{orderLink ? (
									<Link
										href={orderLink}
										className="font-semibold text-secondary underline-offset-2 hover:underline"
									>
										{orderServiceLabel(ticket.orderId, ticket.orderItemId)}
									</Link>
								) : (
									<p className="font-semibold text-foreground">
										{orderServiceLabel(ticket.orderId, ticket.orderItemId)}
									</p>
								)}
							</div>
						) : null}
						<div>
							<p className="text-xs font-medium text-muted-foreground">Subject</p>
							<p className="font-semibold text-foreground">{ticket.subject}</p>
						</div>
						<div>
							<p className="text-xs font-medium text-muted-foreground">Message</p>
							<p className="whitespace-pre-wrap text-foreground">{ticket.description}</p>
						</div>
					</div>
				</div>

				{ticket.replies.slice(1).map((item, index) => {
					const isOwnReply = idToString(item.authorId) === session?.user.id;
					return (
						<div
							key={`${item.createdAt}-${index}`}
							className={cn("flex w-full flex-col", isOwnReply ? "items-end" : "items-start")}
						>
							<div
								className={cn(
									"max-w-[70%] whitespace-pre-wrap rounded-[1.5rem] px-4 py-2.5 text-sm",
									isOwnReply
										? "rounded-br-none bg-secondary/80 text-secondary-foreground"
										: "rounded-bl-none bg-tertiary/80 text-foreground",
								)}
							>
								{item.message}
							</div>
							<span className="mt-1 px-1 text-[0.65rem] text-grey-foreground">
								{formatTicketDate(item.createdAt)}
							</span>
						</div>
					);
				})}
			</div>

			{ticket.status === SupportTicketStatus.OPEN ? (
				<form className="border-t border-[color:var(--color-border-standard)] bg-background p-4" onSubmit={submitReply}>
					<div className="flex items-end gap-3">
						<Textarea
							value={reply}
							onChange={(event) => setReply(event.target.value)}
							placeholder="Reply to the customer"
							rows={2}
							className="min-h-12 flex-1 resize-none"
						/>
						<Button
							type="submit"
							className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
							disabled={replyState.isLoading || !reply.trim()}
						>
							Send Reply
						</Button>
						<Button
							type="button"
							variant="outline"
							disabled={updateStatusState.isLoading}
							onClick={() => setStatus(SupportTicketStatus.CLOSED)}
						>
							Close
						</Button>
					</div>
				</form>
			) : (
				<div className="flex justify-end border-t border-[color:var(--color-border-standard)] bg-background p-4">
					<Button
						type="button"
						variant="outline"
						disabled={updateStatusState.isLoading}
						onClick={() => setStatus(SupportTicketStatus.OPEN)}
					>
						Reopen Ticket
					</Button>
				</div>
			)}
		</div>
	);
}

function TicketDetailsDialog({
	ticketId,
	staffMode,
	onOpenChange,
}: {
	ticketId: string | null;
	staffMode: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const { toast } = useToast();
	const { data: session } = useSession();
	const [reply, setReply] = useState("");
	const ticketQuery = useGetSupportTicketQuery(
		{ id: ticketId ?? "", viewAs: staffMode ? "staff" : "user" },
		{ skip: !ticketId },
	);
	const [replyTicket, replyState] = useReplySupportTicketMutation();
	const [updateStatus, updateStatusState] = useUpdateSupportTicketStatusMutation();
	const ticket = ticketQuery.data;
	const open = Boolean(ticketId);
	const orderLink = adminOrderHref(ticket?.orderId, ticket?.orderItemId);

	async function submitReply(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (!ticket || !reply.trim()) return;
		try {
			await replyTicket({ id: ticket._id, message: reply.trim(), isStaffReply: staffMode }).unwrap();
			setReply("");
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Could not send reply",
				description: errorIntoString(error),
			});
		}
	}

	async function setStatus(status: SupportTicketStatus) {
		if (!ticket) return;
		try {
			await updateStatus({ id: ticket._id, status }).unwrap();
		} catch (error) {
			toast({
				variant: "destructive",
				title: "Could not update status",
				description: errorIntoString(error),
			});
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[88vh] overflow-y-auto border-white/20 bg-background/85 shadow-2xl backdrop-blur-xl sm:max-w-2xl">
				{ticket ? (
					<>
						<DialogHeader>
							<div className="flex items-start justify-between gap-3">
								<div>
									<DialogTitle>{ticketTitle(ticket)}</DialogTitle>
									<DialogDescription>
										{ticket.subject} - {ticket.category} ticket opened {formatTicketDate(ticket.createdAt)}
									</DialogDescription>
								</div>
								<span
									className={cn(
										"rounded-full px-2.5 py-1 text-xs font-semibold",
										statusClasses(ticket.status),
									)}
								>
									{ticket.status}
								</span>
							</div>
						</DialogHeader>
						<div className="flex flex-col gap-3">
							{staffMode ? (
								<div className="space-y-2 rounded-xl bg-muted/40 p-3 text-xs text-muted-foreground">
									<p>
										Customer: {personLabel(ticket.userId)}
									</p>
									{ticket.orderId ? (
										<p>
											Related order:{" "}
											{orderLink ? (
												<Link
													href={orderLink}
													className="font-semibold text-secondary underline-offset-2 hover:underline"
												>
													{orderServiceLabel(ticket.orderId, ticket.orderItemId)}
												</Link>
											) : (
												<span className="font-semibold text-foreground">
													{orderServiceLabel(ticket.orderId, ticket.orderItemId)}
												</span>
											)}
										</p>
									) : null}
								</div>
							) : null}
							{ticket.replies.map((item, index) => {
								const isOwnReply = idToString(item.authorId) === session?.user.id;
								return (
									<div
										key={`${item.createdAt}-${index}`}
										className={cn(
											"max-w-[80%] rounded-2xl p-3 text-sm",
											isOwnReply
												? "self-end bg-secondary/10 text-foreground"
												: "self-start bg-muted/60 text-foreground",
										)}
									>
										<div className="mb-1 flex items-center justify-between gap-2 text-xs text-muted-foreground">
											<span>
												{isStaffSideReply(item)
													? "Techxeed Support"
													: personName(item.authorId)}
											</span>
											<span>{formatTicketDate(item.createdAt)}</span>
										</div>
										<p className="whitespace-pre-wrap">{item.message}</p>
									</div>
								);
							})}
						</div>
						{ticket.status === SupportTicketStatus.OPEN ? (
							<form className="mt-4 space-y-3" onSubmit={submitReply}>
								<Textarea
									value={reply}
									onChange={(event) => setReply(event.target.value)}
									placeholder={staffMode ? "Reply to the customer" : "Add more information"}
									rows={4}
								/>
								<div className="flex flex-wrap justify-end gap-2">
									{staffMode ? (
										<Button
											type="button"
											variant="outline"
											disabled={updateStatusState.isLoading}
											onClick={() => setStatus(SupportTicketStatus.CLOSED)}
										>
											Close Ticket
										</Button>
									) : null}
									<Button
										type="submit"
										className="bg-secondary text-secondary-foreground hover:bg-secondary/90"
										disabled={replyState.isLoading || !reply.trim()}
									>
										Send Reply
									</Button>
								</div>
							</form>
						) : staffMode ? (
							<div className="mt-4 flex flex-wrap items-center justify-center gap-2 rounded-xl bg-muted/50 p-3 text-sm text-muted-foreground">
								<div className="flex items-center gap-2">
									<span>This ticket is closed</span>
									<SupportTicketStatusDropdown
										status={ticket.status}
										disabled={updateStatusState.isLoading}
										onChange={setStatus}
									/>
								</div>
							</div>
						) : (
							<div className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-muted/50 p-3 text-sm text-muted-foreground">
								<span>This ticket is closed</span>
								<SupportTicketStatusDropdown
									status={ticket.status}
									disabled={updateStatusState.isLoading}
									onChange={setStatus}
								/>
							</div>
						)}
					</>
				) : (
					<>
						<DialogHeader>
							<DialogTitle>Support ticket details</DialogTitle>
							<DialogDescription>Loading ticket details...</DialogDescription>
						</DialogHeader>
						<p className="py-8 text-center text-sm text-muted-foreground">Loading ticket...</p>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
